import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Ticket, Download, ChevronDown, ChevronRight, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const peso = (n) => `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const scheduleLabel = (r) => {
  if (!r.event_date) return r.time || '';
  const d = new Date(r.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${d} • ${r.time}${r.end_time ? ` – ${r.end_time}` : ''}`;
};

const EventGuests = () => {
  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [canViewRevenue, setCanViewRevenue] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${API}/admin/event-guests`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setRows(res.data.events);
      setCanViewRevenue(res.data.can_view_revenue !== false);
    } catch (error) {
      console.error('Error fetching event guests:', error);
      toast.error('Failed to load event guests');
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const grandTotal = rows.reduce((sum, r) => sum + Number(r.total_payout || 0), 0);
  const grandGuests = rows.reduce((sum, r) => sum + Number(r.confirmed_guests || 0), 0);

  const handleCheckIn = async (guest) => {
    if (!guest.id) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/bookings/${guest.id}/attendance`, { attended: !guest.attended }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(guest.attended ? `${guest.name} marked absent` : `${guest.name} checked in`);
      fetchData();
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error('Failed to update check-in');
    }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Event', 'Schedule', 'Guest', 'Email', 'Phone', 'Add-ons', 'Notes', 'Terms Ticked', 'Terms Not Ticked'];
    if (canViewRevenue) header.push('Amount Paid');
    header.push('Booking Date', 'Source');
    const lines = [header.join(',')];
    rows.forEach((r) => {
      if (r.guests.length === 0) {
        const row = [esc(r.name), esc(scheduleLabel(r)), esc('— no confirmed guests —'), '', '', '', '', '', ''];
        if (canViewRevenue) row.push('0.00');
        row.push('', '');
        lines.push(row.join(','));
      } else {
        r.guests.forEach((g) => {
          const row = [esc(r.name), esc(scheduleLabel(r)), esc(g.name), esc(g.email), esc(g.phone), esc((g.addons || []).join('; ')), esc(g.notes), esc((g.terms_ticked || []).join('; ')), esc((g.terms_unticked || []).join('; '))];
          if (canViewRevenue) row.push(Number(g.amount || 0).toFixed(2));
          row.push(esc(g.booking_date), esc(g.source));
          lines.push(row.join(','));
        });
      }
      const totalRow = [esc(`${r.name} — TOTAL`), '', `${r.confirmed_guests} guest(s)`, '', '', '', '', '', ''];
      if (canViewRevenue) totalRow.push(Number(r.total_payout || 0).toFixed(2));
      totalRow.push('', '');
      lines.push(totalRow.join(','));
      lines.push('');
    });
    const grandRow = ['GRAND TOTAL', '', `${grandGuests} guest(s)`, '', '', '', '', '', ''];
    if (canViewRevenue) grandRow.push(grandTotal.toFixed(2));
    grandRow.push('', '');
    lines.push(grandRow.join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event-guests-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8" data-testid="event-guests">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'var(--heading-font)' }}>
            <Ticket size={28} /> Event Guests
          </h2>
          <p className="text-sm text-zinc-500 mt-1">Confirmed ticket holders and expected payouts per event.</p>
        </div>
        <button onClick={exportCsv} className="bg-black text-white rounded-full px-6 py-3 font-bold flex items-center gap-2 hover:bg-zinc-800 transition-colors" data-testid="event-guests-export">
          <Download size={18} /> EXPORT CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div>
          <Label htmlFor="eg-start">From (event date)</Label>
          <Input id="eg-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="event-guests-start-date" />
        </div>
        <div>
          <Label htmlFor="eg-end">To (event date)</Label>
          <Input id="eg-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="event-guests-end-date" />
        </div>
        {(startDate || endDate) && (
          <button onClick={() => { setStartDate(''); setEndDate(''); }} className="text-sm underline text-zinc-600" data-testid="event-guests-clear-filters">
            Clear filters
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="mb-6 flex flex-wrap items-center gap-8 bg-black text-white p-4 rounded" data-testid="event-guests-summary">
        {canViewRevenue && (
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-400">Total Expected Payout</p>
            <p className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }} data-testid="event-guests-grand-total">{peso(grandTotal)}</p>
          </div>
        )}
        <div>
          <p className="text-xs uppercase tracking-widest text-zinc-400">Total Confirmed Guests</p>
          <p className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }} data-testid="event-guests-grand-count">{grandGuests}</p>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-zinc-500" data-testid="event-guests-empty">No events found.</p>
      ) : (
        <div className="overflow-x-auto border border-zinc-200 rounded">
          <table className="w-full">
            <thead className="bg-zinc-100">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-bold w-8"></th>
                <th className="px-6 py-3 text-left text-sm font-bold">Event</th>
                <th className="px-6 py-3 text-left text-sm font-bold">Schedule</th>
                <th className="px-6 py-3 text-left text-sm font-bold">Guests</th>
                {canViewRevenue && <th className="px-6 py-3 text-right text-sm font-bold">Payout</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.event_id}>
                  <tr className="border-t border-zinc-200 hover:bg-zinc-50 cursor-pointer" onClick={() => toggleExpand(r.event_id)} data-testid={`event-guest-row-${r.event_id}`}>
                    <td className="px-6 py-3">{expanded.has(r.event_id) ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</td>
                    <td className="px-6 py-3 font-medium">{r.name}</td>
                    <td className="px-6 py-3 text-zinc-600">{scheduleLabel(r)}</td>
                    <td className="px-6 py-3 text-zinc-600">
                      {r.confirmed_guests}{r.capacity ? ` / ${r.capacity}` : ''}
                    </td>
                    {canViewRevenue && <td className="px-6 py-3 text-right font-medium">{peso(r.total_payout)}</td>}
                  </tr>
                  {expanded.has(r.event_id) && (
                    <tr className="bg-zinc-50" data-testid={`event-guest-detail-${r.event_id}`}>
                      <td colSpan={canViewRevenue ? 5 : 4} className="px-6 py-4">
                        {r.guests.length === 0 ? (
                          <p className="text-sm text-zinc-500">No confirmed guests yet.</p>
                        ) : (
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-zinc-500 border-b border-zinc-200">
                                <th className="py-2 text-left font-semibold">Name</th>
                                <th className="py-2 text-left font-semibold">Email</th>
                                <th className="py-2 text-left font-semibold">Add-ons</th>
                                <th className="py-2 text-left font-semibold">Notes</th>
                                {canViewRevenue && <th className="py-2 text-right font-semibold">Amount Paid</th>}
                                <th className="py-2 text-right font-semibold">Check-In</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.guests.map((g, i) => (
                                <tr key={i} className="border-b border-zinc-100" data-testid={`event-guest-detail-row-${r.event_id}-${i}`}>
                                  <td className="py-2 font-medium">{g.name} {g.source === 'walk-in' && <span className="text-[10px] text-blue-600 border border-blue-300 rounded px-1 ml-1">walk-in</span>}</td>
                                  <td className="py-2 text-zinc-600">{g.email || '—'}</td>
                                  <td className="py-2 text-zinc-600">{(g.addons && g.addons.length) ? g.addons.join(', ') : '—'}</td>
                                  <td className="py-2 text-zinc-600 max-w-[200px]" data-testid={`event-guest-notes-${r.event_id}-${i}`}>{g.notes || '—'}</td>
                                  {canViewRevenue && <td className="py-2 text-right font-medium">{peso(g.amount)}</td>}
                                  <td className="py-2 text-right">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleCheckIn(g); }}
                                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${g.attended ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                                      data-testid={`event-guest-checkin-${r.event_id}-${i}`}
                                    >
                                      {g.attended ? <><UserCheck size={14} /> Present</> : <><UserX size={14} /> Check In</>}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                              <tr className="font-bold">
                                <td className="py-2" colSpan={canViewRevenue ? 5 : 4}>Total ({r.confirmed_guests} guest{r.confirmed_guests === 1 ? '' : 's'})</td>
                                {canViewRevenue && <td className="py-2 text-right">{peso(r.total_payout)}</td>}
                              </tr>
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EventGuests;
